import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createLogger } from '../../../utils/logger';

const logger = createLogger('QuestionImage');

/**
 * クイズの問題文画像を表示するコンポーネント
 * Phase 1: 画像ローディング最適化実装
 */
const QuestionImage = ({ imageData, alt = '問題画像', className = '' }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // メモリクリーンアップ対応
  useEffect(() => {
    return () => {
      // コンポーネントアンマウント時のクリーンアップ
      if (imageData?.url && imageData.url.startsWith('blob:')) {
        URL.revokeObjectURL(imageData.url);
      }
    };
  }, [imageData?.url]);

  if (!imageData || !imageData.url) {
    return null;
  }

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
    
    logger.debug('画像ロード完了', {
      filename: imageData.filename,
      url: imageData.url,
      timestamp: new Date().toISOString()
    });
  };

  const handleImageError = (error) => {
    setIsLoading(false);
    setHasError(true);
    
    logger.error('画像ロードエラー', {
      filename: imageData.filename,
      url: imageData.url,
      error: error.target?.error || 'Unknown error',
      timestamp: new Date().toISOString()
    });
  };

  return (
    <div className={`relative ${className}`}>
      {isLoading && (
        <div className="flex items-center justify-center h-48 bg-gray-100 rounded-lg">
          {/* 改善されたローディング表示: スピニングアニメーション */}
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
            <div className="text-gray-500">画像を読み込み中...</div>
          </div>
        </div>
      )}
      
      {hasError && (
        <div className="flex items-center justify-center h-48 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300">
          <div className="text-center text-gray-500">
            <div className="mb-2">⚠️</div>
            <div>画像を読み込めませんでした</div>
          </div>
        </div>
      )}

      <img
        src={imageData.url}
        alt={alt}
        className={`w-full max-w-full h-auto rounded-lg shadow-sm border border-gray-200 ${
          isLoading || hasError ? 'hidden' : 'block'
        }`}
        onLoad={handleImageLoad}
        onError={handleImageError}
        style={{ maxHeight: '400px', objectFit: 'contain' }}
        // Phase 1: 画像属性の最適化
        fetchpriority="high"
        decoding="async"
        referrerPolicy="no-referrer"
      />

      {imageData.filename && !isLoading && !hasError && (
        <div className="mt-2 text-xs text-gray-500 text-center">
          {imageData.filename}
        </div>
      )}
    </div>
  );
};

QuestionImage.propTypes = {
  imageData: PropTypes.shape({
    url: PropTypes.string.isRequired,
    filename: PropTypes.string,
    uploadedAt: PropTypes.number
  }),
  alt: PropTypes.string,
  className: PropTypes.string
};

export default QuestionImage; 